import { AfterViewInit, Component, ElementRef, Injectable, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { BehaviorSubject, combineLatest, defer, of, Subject } from 'rxjs';
import { ActivatedRouteSnapshot, CanActivate, GuardResult, MaybeAsync, Router, RouterStateSnapshot } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { catchError, filter, finalize, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { AuthService, ContactUser } from '@aiwyn/auth';
import { AssetUriService, ConfigService } from '@aiwyn/config';
import { Store } from '@ngxs/store';
import { getFirstOfQueryList, ignoreFalsy, ignoreNullish, LoadableHelper, Logger } from '@aiwyn/shared';
import { CustomAnimation } from '../custom-animation';
import { LegalService } from '../../../../services/legal.service';
import { ROUTE_TOKEN_LOGIN, ROUTE_TOKEN_MAIN_BASE } from '../../../../routing-constants';
import { ModalAlertComponent } from '../../../main/modals/alert/modal-alert.component';
import { MatDialog } from '@angular/material/dialog';

@Injectable({
  providedIn: 'root'
})
export class RegisterContactGuard implements CanActivate {

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly logger: Logger,
  ) { }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): MaybeAsync<GuardResult> {
    return this.authService.getAuthProviderUser().pipe(
      filter(({ status }) => LoadableHelper.isLoaded(status)),
      take(1),
      switchMap(({ data: providerUser }) => {
        // Registration happens with a partial
        if (!providerUser) {
          this.logger.info('RegisterContactGuard :: canActivate :: no user, redirecting to login.');
          return of(this.router.createUrlTree([ROUTE_TOKEN_LOGIN]));
        }

        if (providerUser.isAnonymous()) {
          this.logger.info('RegisterContactGuard :: canActivate :: user is anonymous, cannot register. Redirecting to main.');
          return of(this.router.createUrlTree([ROUTE_TOKEN_MAIN_BASE]))
        }

        return this.authService.getUser().pipe(
          take(1),
          map(applicationUser => {
            // If the user has clients and has accepted the terms, they are considered registered already.
            if (!(applicationUser instanceof ContactUser)
              || !!applicationUser.isFullyRegistered()
            ) {
              this.logger.info('RegisterContactGuard :: canActivate :: user has already registered, redirecting to main.');
              return this.router.createUrlTree([ROUTE_TOKEN_MAIN_BASE]);
            }

            this.logger.info('RegisterContactGuard :: canActivate :: Contact user has not yet registered, allowing navigation');
            return true;
          }),
        );
      }),
    );
  }
}

@Component({
  selector: 'app-register-contact',
  templateUrl: './register-contact.component.html',
  styleUrls: ['./register-contact.component.scss'],
  animations: CustomAnimation,
  standalone: false,
})
export class RegisterContactComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly _unsubscribe$ = new Subject<void>();

  @ViewChildren('fullName') fullNameInput: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('clientKey') clientKeyInput: QueryList<ElementRef<HTMLInputElement>>;

  private failedClientKeyAttempts = 0;
  private readonly failedAttemptsAllowed: number = 5;
  public userEmail = '';
  clientKeyValid = true;
  registerContactForm: UntypedFormGroup;
  displayName = '!';
  clientKeyLabel = '';

  initializeForm$ = this.authService.getUser().pipe(
    ignoreNullish(),
    take(1),
    // Prepopulate from user
    tap(user => {
      if (!(user instanceof ContactUser)) {
        this.logger.error('RegisterContactComponent :: initializeForm$ :: user is not a contact user, cannot proceed with form prepopulation');
        throw new Error('Non contact user is attempting to register via the register contact flow');
      }
      this.userEmail = user.getProviderEmail();
      this.registerContactForm = this.getFormBuilder(user);
      this.isFormLoading$.next(false);
    }),
    switchMap(_ => this.didViewInit$),
    ignoreFalsy(),
    switchMap(_ => combineLatest([
      getFirstOfQueryList(this.fullNameInput, false),
      getFirstOfQueryList(this.clientKeyInput),
    ])),
    take(1),
    tap(([fullNameInput, clientKeyInput]) => (fullNameInput ?? clientKeyInput)?.nativeElement.focus()),
    takeUntil(this._unsubscribe$),
  );

  isFormLoading$ = new BehaviorSubject<boolean>(true);
  didViewInit$ = new BehaviorSubject<boolean>(false);
  isSubmissionProcessing$ = new BehaviorSubject<boolean>(false);

  isUserFullyRegistered$ = this.authService.getUser().pipe(
    map(user => user && user instanceof ContactUser && user.isFullyRegistered()),
  );

  constructor(
    public authService: AuthService,
    public assetsUriService: AssetUriService,
    public configService: ConfigService,
    private formBuilder: UntypedFormBuilder,
    private store: Store,
    private readonly logger: Logger,
    public readonly legalService: LegalService,
    private readonly dialog: MatDialog,
    private readonly router: Router,
  ) { }

  ngOnInit(): void {
    // get client key text for display fields
    this.clientKeyLabel = this.configService.clientKeyLabel;

    this.initializeForm$.subscribe();

    this.authService.getUser().pipe(
      tap(user => {
        this.displayName = ' back!';
        if (user) {
          this.displayName = `, ${user.getDisplayName()}!`;
        }
      }),
      takeUntil(this._unsubscribe$),
    );
  }

  ngAfterViewInit(): void {
    this.didViewInit$.next(true);
  }

  getFormBuilder(user: ContactUser): UntypedFormGroup {
    const fullNameControlEntry: Record<string, unknown> = !user.isNameValid() ?
      { fullName: ['', [Validators.required]] } :
      { };

    const acceptTermsEntry = !user.getContact().acceptedTermsAt
      ? { terms: [false, Validators.requiredTrue] }
      : {};

    // If we already have the Client Id, then we just need the Display Name or to Accept Terms
    if (user.getClientIdList().length > 0) {
      return this.formBuilder.group({
        ...fullNameControlEntry,
        ...acceptTermsEntry
      });
    }
    if (this.configService.relaxedModeEnabled) {
      return this.formBuilder.group({
        ...fullNameControlEntry,
        ...acceptTermsEntry,
        clientName: ['', [Validators.required]],
        clientKey: ['', [Validators.required]],
      });
    }

    return this.formBuilder.group({
      ...fullNameControlEntry,
      ...acceptTermsEntry,
      invoiceNumber: [''],
      clientKey: ['', [Validators.required]],
    });
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  goBack(): void {
    // Clarification needed. Can users proceed without fully registering?
    this.signOut();
  }

  signOut(): void {
    this.authService.signOut().pipe(
      take(1)
    ).subscribe();
  }

  submit(): void {
    // Mark everything as touched so we can show the user what they missed
    this.registerContactForm.markAllAsTouched();
    this.registerContactForm.updateValueAndValidity();

    // Don't do anything if the Form isn't valid
    if (!this.registerContactForm.valid) {
      return;
    }

    this.isSubmissionProcessing$.next(true);

    this.authService.contactRegistrationPostProcessing(
      defer(() => {
        // If we aren't setting the Client Key, then just don't do anything...
        if (!this.registerContactForm.controls.clientKey) {
          return of(null);
        }

        // User is known, and attempting to add a client to their account
        return this.configService.relaxedModeEnabled ?
          // When relaxed mode is enabled, client can be created on the fly.
          this.authService.addClientToUserWithRelaxEnabled(
            this.registerContactForm.controls.clientKey.value,
            this.registerContactForm.controls.clientName.value,
          ) :
          // When relaxed mode is not enabled, client key and invoice number are required.
          this.authService.addClientToUser(
            this.registerContactForm.controls.clientKey.value,
            this.registerContactForm.controls.invoiceNumber.value,
          );
      }),
      this.registerContactForm.controls.fullName?.value,
      !!this.registerContactForm.controls.terms,
    ).pipe(
      tap(_ => this.router.navigate([ROUTE_TOKEN_MAIN_BASE])),
      catchError(error => {
        if (error.message === 'Failed to add client key') {
          this.clientKeyValid = false;
          this.failedClientKeyAttempts++;

          return this.dialog.open(
            ModalAlertComponent,
            {
              data: {
                title: 'Client Key Error',
                message: 'The client key entered is either not valid or the client is inactive.',
              }
            }
          ).afterClosed().pipe(
            switchMap(_ => {
              if (this.failedClientKeyAttempts >= this.failedAttemptsAllowed) {
                return this.authService.signOut();
              }

              return of(undefined);
            }),
          );
        }

        // General error handling for bad registration update
        return this.dialog.open(
          ModalAlertComponent,
          {
            data: {
              title: 'Registration Error',
              message: 'An error occurred during registration. Please attempt your request again later.',
            }
          }
        ).afterClosed();
      }),
      take(1),
      finalize(() => this.isSubmissionProcessing$.next(false)),
    ).subscribe();
  }
}
